/**
 * Build the URL for a PH10 endpoint whose key is a PowerHouse business code.
 *
 * **Why (card 118, Chris ruled (b) 2026-09-08).** PowerHouse lets a code contain any
 * character. `owner_id`, `item_id`, `location_id`, `mover_id`, `carrier_id` and about forty
 * other key types are free text. A code with `\`, `/` or `%` in it URL-encodes to `%5C`, `%2F`
 * or `%25`, and Tomcat rejects those inside a URL **path segment** with a raw HTML 400 —
 * *before* Spring, before our code, before any error handler. The record then cannot be opened,
 * edited or deleted and the tester sees a bare 400 with no message. Russ on MYRX hit exactly
 * this trying to delete the mover `R\FS-100` (slice-3519, verified live).
 *
 * Every affected backend route now answers on two URLs: the historic
 * `/api/movers/{moverId}` and a key-free sibling in which each `{...}` segment is the literal
 * `_`, with the key carried in the query string, which handles all three characters correctly.
 * This helper writes the sibling form from the SAME template string the backend declares, so
 * a call site never has to know the rule:
 *
 * ```ts
 * keyedPath('/api/movers/{moverId}', { moverId })
 * //  moverId = 'R\FS-100'  ->  '/api/movers/_?moverId=R%5CFS-100'
 *
 * keyedPath('/api/owners/{ownerId}/items/{itemId}', { ownerId, itemId }, { q: 'x' })
 * //  '/api/owners/_/items/_?ownerId=ACME&itemId=A%2FB&q=x'
 * ```
 *
 * The Java mirror is `platform/http/KeyedRoutes.sibling(String)`; the pin test
 * `PathKeyedRouteSweepTest` fails if a controller adds a path-keyed free-text route without
 * its sibling, and `keyedPath.test.ts` pins the two spellings agreeing.
 */

/** The literal segment that stands in for a key. Mirrors `KeyedRoutes.PLACEHOLDER`. */
export const KEY_PLACEHOLDER = '_';

export type KeyValues = Record<string, string | number | boolean | null | undefined>;

/**
 * Replace every `{name}` segment of `template` with `_` and move the value into the query
 * string.
 *
 * @param template the backend's path template, e.g. `/api/movers/{moverId}/rf-screens`
 * @param keys     one entry per `{name}` in the template — a missing or blank key throws,
 *                 because the backend answers a blank key with a 400 and a silent
 *                 `?ownerId=` would otherwise address the row whose key is the empty string
 * @param extra    ordinary query parameters to append (undefined/null entries are dropped)
 */
export function keyedPath(template: string, keys: KeyValues, extra: KeyValues = {}): string {
  const names: string[] = [];
  const path = template
    .split('/')
    .map((seg) => {
      if (!seg.startsWith('{') || !seg.endsWith('}')) return seg;
      names.push(seg.slice(1, -1));
      return KEY_PLACEHOLDER;
    })
    .join('/');

  if (names.length === 0) {
    throw new Error(`keyedPath: template has no {key} segment: ${template}`);
  }

  const qs = new URLSearchParams();
  for (const name of names) {
    const value = keys[name];
    if (value === undefined || value === null || String(value) === '') {
      throw new Error(`keyedPath: missing value for {${name}} in ${template}`);
    }
    qs.set(name, String(value));
  }
  for (const [name, value] of Object.entries(extra)) {
    if (value === undefined || value === null) continue;
    qs.set(name, String(value));
  }
  return `${path}?${qs.toString()}`;
}

/**
 * Append an already-built query fragment to a `keyedPath()` URL.
 *
 * Card 118, slice-3602. Many PH10 call sites build their query string separately — a
 * `URLSearchParams` they `.toString()`, or a small local `qs(...)` / `whQs(...)` helper that
 * returns `'?warehouseId=DAR01'` or `''`. Those cannot move into `keyedPath`'s `extra` object
 * without rewriting the helper, and concatenating them by hand is where the bug lives:
 * `keyedPath()` always ends in `?key=…`, so a naive `` `${keyedPath(...)}${qs}` `` yields
 * `?ownerId=ACME?warehouseId=DAR01` and the warehouse is silently dropped.
 *
 * `withQuery` normalises the join: a leading `?` or `&` on the fragment is stripped, an empty
 * or blank fragment is a no-op, and the separator is chosen from the URL it is appending to.
 *
 * ```ts
 * withQuery(keyedPath('/api/items/{ownerId}/{itemId}/status/lost', { ownerId, itemId }), whQs(w))
 * //  '/api/items/_/_/status/lost?ownerId=ACME&itemId=A%2FB&warehouseId=DAR01'
 * ```
 */
export function withQuery(url: string, fragment: string | null | undefined): string {
  if (!fragment) return url;
  const trimmed = fragment.replace(/^[?&]+/, '').replace(/&+$/, '');
  if (trimmed === '') return url;
  return `${url}${url.includes('?') ? '&' : '?'}${trimmed}`;
}
